// Patient Home tab — a staff "Patient tasks" action section on top of the native
// replica of the ACCESS patient companion (sparks/access-patient-beta/companion.html).
// Fed by the coordinator's own get_patient_home read (usePatientHome) — the SAME view
// the companion reads, so the section always matches what the patient sees.
//
// FRAME-SCOPED WRITE INVARIANT (security control — see the 2026-07-22 addendum in
// docs/specs/2026-07-02-patient-home-preview-tab.md): everything inside `.acc-companion`
// stays write-free forever (no ?tid links, no tap-to-complete, no engagement PATCH —
// nothing that could masquerade as a patient action). Staff writes live ONLY in the
// section ABOVE the frame, staff-attributed (actor + updated_by_role) via task_updated.
// No component under patient-file/patient-home/ may import a mutation hook or eventPost.

import React, { useMemo, useState, useEffect } from 'react';
import { usePatientHome } from '@/state/data/patientHome';
import { usePatientChatLastSeen } from '@/state/data/outreach';
import { useCompleteTask, useReopenTask, useResumeTask } from '@/state/data/task';
import { compareOpenTasks, completionActorOf } from '@/logic/task';
import {
  filterPatientVisibleTasks,
  filterConversationalOutreach,
  stripOutreachAuthorLastName,
  scrubCarePlanApproval,
  patientActionableTasks,
  patientUpcomingTasks,
  patientCompletedTasks,
  applyTaskOverride,
  overrideSettled,
  unreadForPatient,
} from '@/logic/patientHome';
import '@/styles/patient-home-companion.css';
import TaskCard from '../shared/TaskCard';
import EmptyState from '../shared/EmptyState';
import HeroHeader from '../patient-home/HeroHeader';
import NextSteps from '../patient-home/NextSteps';
import ShareInviteCard from '../patient-home/ShareInviteCard';
import CareTeamMessages from '../patient-home/CareTeamMessages';
import LearningPath from '../patient-home/LearningPath';
import BpChart from '../patient-home/BpChart';
import WeightChart from '../patient-home/WeightChart';
import YourPlan from '../patient-home/YourPlan';
import Footer from '../patient-home/Footer';

// The companion is a phone surface — render it inside a fixed-width, centered frame
// (narrower than the 430px spark width: it sits BELOW the task section as a preview).
// position:relative so any absolute in-frame element stays clipped to the phone.
const FRAME_STYLE = { position: 'relative', maxWidth: 360, margin: '0 auto', overflow: 'hidden' };

// Optimistic overlay TTL — a write the server never confirms (silent WF17 failure)
// reverts to server truth after this window instead of masking reality forever.
const OVERRIDE_TTL_MS = 30_000;

function Frame({ children }) {
  return (
    <div className="acc-companion">
      <div style={FRAME_STYLE}>
        <div className="app" style={{ opacity: 1 }}>{children}</div>
      </div>
    </div>
  );
}

function CompanionSkeleton() {
  return (
    <div className="patient-file-companion-loading" style={{ padding: 24, maxWidth: 430, margin: '0 auto' }}>
      <div className="skeleton-bar" style={{ width: '100%', height: 160, borderRadius: 16, marginBottom: 20 }} />
      {[1, 2, 3].map((i) => (
        <div key={i} className="skeleton-bar" style={{ width: '100%', height: 90, borderRadius: 12, marginBottom: 14 }} />
      ))}
    </div>
  );
}

const pillStyle = (selected) => ({
  background: selected ? 'var(--wi-background-tertiary)' : 'transparent',
  color: selected ? 'var(--wi-text-primary)' : 'var(--wi-text-secondary)',
  border: 'none', borderRadius: 20, padding: '3px 10px', cursor: 'pointer',
  fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
  fontFamily: 'var(--wi-font-family)', display: 'inline-flex', alignItems: 'center', gap: 6,
});

export default function PatientHomeTab({
  patient, carePeriod, currentUser, userRole, onCloseTask, onSnoozeTask = null, onToast,
}) {
  const userid = patient?.userid;
  const firstName = patient?.firstname || '';
  const { data, isLoading, error } = usePatientHome(userid);
  const completeTaskMut = useCompleteTask();
  const reopenTaskMut = useReopenTask();
  const resumeTaskMut = useResumeTask();

  const [taskTab, setTaskTab] = useState('active'); // 'active' | 'done'
  // Optimistic overlay — set once the Tier-4 write is ACKNOWLEDGED (apiv2 forwards it; WF17's
  // verdict never reaches the browser); dropped once the refetched view agrees
  // (overrideSettled), or after OVERRIDE_TTL_MS so a silently-failed write resurfaces.
  const [overrides, setOverrides] = useState({});
  useEffect(() => { setOverrides({}); setTaskTab('active'); }, [userid]);

  // Fidelity filters (COSMETIC — mirror what the patient sees; not security):
  //  · tasks CARRY visible_to_patient → filter faithfully
  //  · outreach → drop non-conversational channels (email/ehr_message) the patient
  //    never sees in chat, then strip author_lastname. No visible_to_patient in the
  //    projection, so this is the best-effort mirror (documented limit).
  //  · care_plan → hide approved_by/at
  const patientTasks = useMemo(() => filterPatientVisibleTasks(data?.open_tasks), [data]);
  const outreach = useMemo(
    () => stripOutreachAuthorLastName(filterConversationalOutreach(data?.outreach_tail)),
    [data],
  );
  const carePlan = useMemo(() => scrubCarePlanApproval(data?.care_plan ?? null), [data]);
  // Computed here, not apiv2's unread_count: that one counts the patient's own clinic-line texts.
  const lastSeenQuery = usePatientChatLastSeen(userid, { refetchInterval: 60_000 });
  // `data` survives a failed refetch, so a blip keeps the last good count; only a read that never succeeded is unknown.
  const markerRead = lastSeenQuery.data !== undefined;
  const unread = useMemo(
    () => (markerRead ? unreadForPatient(data?.outreach_tail, lastSeenQuery.data) : null),
    [data, markerRead, lastSeenQuery.data],
  );
  const unreadUnknown = !markerRead && lastSeenQuery.isError;

  // Drop overrides the server now confirms (or that expired) whenever fresh data lands.
  useEffect(() => {
    setOverrides((prev) => {
      const now = Date.now();
      const next = {};
      Object.entries(prev).forEach(([id, o]) => {
        const row = patientTasks.find((t) => String(t.task_id) === id);
        const agreed = row && overrideSettled(row, o.override);
        if (!agreed && now - o.at <= OVERRIDE_TTL_MS) next[id] = o;
      });
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [patientTasks]);

  const mergedTasks = useMemo(() => {
    const now = Date.now();
    return patientTasks.map((t) => {
      const o = overrides[String(t.task_id)];
      if (!o || overrideSettled(t, o.override) || now - o.at > OVERRIDE_TTL_MS) return t;
      return applyTaskOverride(t, o.override);
    });
  }, [patientTasks, overrides]);

  const activeTasks = useMemo(() => patientActionableTasks(mergedTasks).slice().sort(compareOpenTasks), [mergedTasks]);
  const upcomingCount = useMemo(() => patientUpcomingTasks(mergedTasks).length, [mergedTasks]);
  const doneTasks = useMemo(() => patientCompletedTasks(mergedTasks).slice(0, 15), [mergedTasks]);

  const setOverride = (taskId, override) => {
    setOverrides((prev) => ({ ...prev, [String(taskId)]: { override, at: Date.now() } }));
    // TanStack structural sharing keeps `data` referentially stable when a refetch
    // returns identical rows (the silently-failed-write case) — nothing would ever
    // re-evaluate the TTL. Force an expiry pass so server truth resurfaces.
    setTimeout(() => {
      setOverrides((prev) => {
        const now = Date.now();
        const next = Object.fromEntries(Object.entries(prev).filter(([, o]) => now - o.at <= OVERRIDE_TTL_MS));
        return Object.keys(next).length === Object.keys(prev).length ? prev : next;
      });
    }, OVERRIDE_TTL_MS + 500);
  };

  async function handleComplete(taskId) {
    const actor = completionActorOf(currentUser, userRole);
    try {
      await completeTaskMut.mutateAsync({ taskId, notes: '', withingsUserId: userid, actor });
      setOverride(taskId, { kind: 'status', patch: { status: 'completed', completed_at: new Date().toISOString(), completion_source: 'staff' } });
      onToast?.('Task marked done');
      return true; // TaskCard keeps the checkbox settled only on a real completion.
    } catch (e) {
      onToast?.('Error: ' + e.message);
    }
    return false;
  }

  async function handleReopen(taskId) {
    const row = mergedTasks.find((t) => String(t.task_id) === String(taskId));
    // A future available_at goes back to 'scheduled' — never surface a task in the
    // patient companion before its reveal date.
    const toStatus = row?.available_at && new Date(row.available_at) > new Date() ? 'scheduled' : 'pending';
    try {
      await reopenTaskMut.mutateAsync({
        taskId, withingsUserId: userid, toStatus, actor: completionActorOf(currentUser, userRole),
      });
      setOverride(taskId, { kind: 'status', patch: { status: toStatus, completed_at: null, completion_source: null } });
      onToast?.('Task reopened');
    } catch (e) {
      onToast?.('Error: ' + e.message);
    }
  }

  // Snooze / Bring back — the same Tier-4 `changes.snooze` write the staff surfaces use
  // (WF17 owns the guard rails). The shell owns the modal, like Cancel's confirm, and hands
  // the result back through onSnoozed so the overlay lands here rather than in the staff bundle.
  function handleSnoozed(task, until, reason) {
    setOverride(task.task_id, {
      kind: 'snooze', until, reason, actor: completionActorOf(currentUser, userRole),
    });
    onToast?.(`Snoozed until ${until}`);
  }

  async function handleResume(task) {
    try {
      await resumeTaskMut.mutateAsync({
        taskId: task.task_id, withingsUserId: userid, actor: completionActorOf(currentUser, userRole),
      });
      setOverride(task.task_id, { kind: 'resume' });
      onToast?.('Snooze removed');
    } catch (e) {
      onToast?.('Error: ' + e.message);
    }
  }

  if (isLoading) return <CompanionSkeleton />;

  if (error) {
    return (
      <div className="patient-detail-empty" style={{ padding: 40, textAlign: 'center' }}>
        <p className="body2 text-secondary">Couldn’t load this patient’s home preview.</p>
        <p className="detail1 text-placeholder">{String(error?.message || error)}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="patient-detail-empty" style={{ padding: 40, textAlign: 'center' }}>
        <p className="body2 text-secondary">No patient home to preview for this patient yet.</p>
      </div>
    );
  }

  // The view returns care_period directly (pre-alignment = null). Prefer it; fall back
  // to the shell's carePeriod prop only for the enrollment-date footer if present.
  const homeCarePeriod = data.care_period ?? null;
  const footerCarePeriod = homeCarePeriod || (carePeriod
    ? { care_period_start: carePeriod.care_period_start || carePeriod.start_date }
    : null);

  return (
    <>
      {/* ── Patient tasks — staff actions on what the patient sees ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 10px' }}>
        <span className="eyebrow text-secondary">Patient tasks</span>
        <button type="button" onClick={() => setTaskTab('active')} aria-pressed={taskTab === 'active'} style={pillStyle(taskTab === 'active')}>
          Active{activeTasks.length > 0 ? ` (${activeTasks.length})` : ''}
        </button>
        <button type="button" onClick={() => setTaskTab('done')} aria-pressed={taskTab === 'done'} style={pillStyle(taskTab === 'done')}>
          Done{doneTasks.length > 0 ? ` (${doneTasks.length})` : ''}
        </button>
      </div>
      {taskTab === 'active' ? (
        activeTasks.length === 0 ? (
          <EmptyState compact title="No active patient tasks" />
        ) : (
          <div className="table-component">
            {activeTasks.map((t) => (
              <TaskCard
                key={t.task_id}
                task={t}
                onComplete={handleComplete}
                // onClosed feeds the overlay so the row leaves Active as soon as the
                // shell's confirm lands (a cancelled task vanishes for the patient too).
                onCloseTask={(task) => onCloseTask(task, {
                  onClosed: (id) => setOverride(id, { kind: 'status', patch: { status: 'cancelled' } }),
                })}
                onSnooze={onSnoozeTask ? (task) => onSnoozeTask(task, { onSnoozed: handleSnoozed }) : null}
                onResume={onSnoozeTask ? handleResume : null}
                viewerRole={userRole}
                suppressRole="patient"
              />
            ))}
          </div>
        )
      ) : (
        doneTasks.length === 0 ? (
          <EmptyState compact title="No patient tasks completed yet" />
        ) : (
          <div className="table-component">
            {doneTasks.map((t) => (
              <TaskCard key={t.task_id} task={t} onReopen={handleReopen} suppressRole="patient" />
            ))}
          </div>
        )
      )}
      {upcomingCount > 0 && (
        <p className="detail1 text-secondary" style={{ margin: '8px 2px 0' }}>
          {upcomingCount} upcoming task{upcomingCount > 1 ? 's' : ''} not yet visible to the patient
        </p>
      )}

      {/* ── Patient app preview (the write-free companion replica) ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '24px 0 10px' }}>
        <span className="eyebrow text-secondary">Patient app preview</span>
      </div>
      {/* The frame renders server truth (patientTasks, not mergedTasks) — the preview
          shows what the patient sees RIGHT NOW, before the WF17 write lands. */}
      <Frame>
        <HeroHeader tasks={patientTasks} carePeriod={homeCarePeriod} />
        <div className="acc-content-panel">
          <ShareInviteCard tasks={patientTasks} carePeriod={homeCarePeriod} />
          <NextSteps tasks={patientTasks} firstName={firstName} />
          <CareTeamMessages outreach={outreach} unread={unread} unreadUnknown={unreadUnknown} />
          <LearningPath tasks={patientTasks} />
          <BpChart userid={userid} carePlan={carePlan} measureStates={data.measure_states} />
          <WeightChart userid={userid} carePlan={carePlan} measureStates={data.measure_states} />
          <YourPlan carePlan={carePlan} measureStates={data.measure_states} />
          <Footer carePeriod={footerCarePeriod} />
        </div>
      </Frame>
    </>
  );
}
